import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const CATEGORIES = [
    { v: "food_drink", t: "Food & Drink" },
    { v: "beauty", t: "Beauty" },
    { v: "wellness", t: "Wellness" },
    { v: "fashion", t: "Fashion" },
    { v: "art", t: "Art" },
    { v: "service", t: "Service" },
    { v: "product", t: "Product" },
    { v: "other", t: "Other" },
];

const categoryLabel = (v) => CATEGORIES.find((c) => c.v === v)?.t || v;

export default function FeaturedArchive() {
    const [past, setPast] = useState([]);
    const [current, setCurrent] = useState(null);
    const [activeCat, setActiveCat] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [p, c] = await Promise.all([
                    api.get("/features/past"),
                    api.get("/features/current"),
                ]);
                setPast(Array.isArray(p.data) ? p.data : []);
                setCurrent(c.data && c.data.business_name ? c.data : null);
            } catch {
                // Non-fatal: the /featured page still renders (empty state) if the API is unreachable.
            }
            finally { setLoading(false); }
        })();
    }, []);

    const visibleCategories = useMemo(() => {
        const seen = new Set(past.map((p) => p.category));
        return [{ v: "all", t: "All" }, ...CATEGORIES.filter((c) => seen.has(c.v))];
    }, [past]);

    const filtered = useMemo(() => {
        if (activeCat === "all") return past;
        return past.filter((p) => p.category === activeCat);
    }, [past, activeCat]);

    return (
        <main className="pt-32 pb-32 min-h-screen" data-testid="featured-archive">
            <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
                <p className="eyebrow">The Archive</p>
                <h1 className="font-display text-6xl md:text-8xl mt-4 max-w-4xl leading-[0.95]">
                    Every business we've <em className="text-[color:var(--gold)]">loved</em>.
                </h1>
                <p className="mt-8 max-w-2xl text-[color:var(--cream-muted)] text-lg">
                    A living gallery of the local businesses, products, and services our community has quietly championed. Their spotlight lives here after their week on the homepage ends.
                </p>
            </section>

            {current && (
                <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-24 border-t border-[color:var(--espresso)] pt-16" data-testid="archive-current">
                    <div className="grid lg:grid-cols-12 gap-10 items-center">
                        {current.photo_url && (
                            <div className="lg:col-span-5 zoom-parent overflow-hidden">
                                <img src={current.photo_url} alt={current.business_name} className="w-full h-[420px] object-cover" />
                            </div>
                        )}
                        <div className={current.photo_url ? "lg:col-span-7" : "lg:col-span-12"}>
                            <p className="text-[0.7rem] uppercase tracking-[0.28em] text-[color:var(--gold)]">On the homepage now · {categoryLabel(current.category)}{current.city ? ` · ${current.city}` : ""}</p>
                            <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[0.95]">{current.business_name}</h2>
                            {current.description && <p className="mt-6 text-[color:var(--cream-muted)] text-lg max-w-xl">{current.description}</p>}
                            <div className="mt-6 flex flex-wrap gap-4 text-xs">
                                {current.instagram && <a href={`https://instagram.com/${current.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="btn-gold btn-gold-solid" data-testid="archive-current-follow">Follow @{current.instagram.replace('@','')}</a>}
                                {current.website && <a href={current.website} target="_blank" rel="noreferrer" className="btn-gold">Visit website</a>}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-32 border-t border-[color:var(--espresso)] pt-16">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <p className="eyebrow">Previously featured</p>
                        <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95]">The full gallery.</h2>
                    </div>
                    <p className="text-xs uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">
                        {loading ? "loading…" : `${filtered.length} feature${filtered.length === 1 ? "" : "s"}`}
                    </p>
                </div>

                <div className="mt-10 flex flex-wrap gap-3" data-testid="archive-filter">
                    {visibleCategories.map((c) => {
                        const active = activeCat === c.v;
                        return (
                            <button
                                key={c.v}
                                onClick={() => setActiveCat(c.v)}
                                data-testid={`archive-chip-${c.v}`}
                                className={
                                    "text-xs uppercase tracking-[0.24em] px-5 py-2 border transition-colors duration-300 " +
                                    (active
                                        ? "bg-[color:var(--gold)] border-[color:var(--gold)] text-[color:var(--warm-black)]"
                                        : "border-[color:var(--espresso)] text-[color:var(--cream-muted)] hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]")
                                }
                            >{c.t}</button>
                        );
                    })}
                </div>

                {!loading && filtered.length === 0 && (
                    <p className="mt-16 text-[color:var(--cream-muted)] text-sm">No features in this category yet.</p>
                )}

                <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                    {filtered.map((p) => (
                        <article key={p.id} className="group" data-testid={`archive-card-${p.id}`}>
                            {p.photo_url ? (
                                <div className="zoom-parent overflow-hidden">
                                    <img src={p.photo_url} alt={p.business_name} className="h-[380px] w-full object-cover" />
                                </div>
                            ) : (
                                <div className="h-[380px] w-full border border-[color:var(--espresso)] flex items-center justify-center text-[color:var(--cream-muted)] text-xs italic">no photo</div>
                            )}
                            <div className="mt-5">
                                <p className="text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]">
                                    {categoryLabel(p.category)}{p.city ? ` · ${p.city}` : ""}
                                </p>
                                <h3 className="font-serif text-2xl mt-2 text-[color:var(--soft-cream)]">{p.business_name}</h3>
                                {p.description && <p className="mt-3 text-sm text-[color:var(--cream-muted)] line-clamp-3">{p.description}</p>}
                                <div className="mt-4 flex flex-wrap gap-4 text-xs">
                                    {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="link-underline uppercase tracking-[0.24em] text-[color:var(--gold)]">Website</a>}
                                    {p.instagram && <a href={`https://instagram.com/${p.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="link-underline uppercase tracking-[0.24em] text-[color:var(--gold)]">@{p.instagram.replace('@','')}</a>}
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-32 border-t border-[color:var(--espresso)] pt-16 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <p className="text-[color:var(--cream-muted)] max-w-xl">Local business, product, or service? Apply to be featured — right here on our homepage. Free, always.</p>
                <Link to="/feature-your-business" className="btn-gold btn-gold-solid" data-testid="archive-apply-cta">Apply to be featured</Link>
            </section>
        </main>
    );
}
